import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Input, Select, message, Space, Popconfirm, Card, Row, Col, Tag, Modal } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BankOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { superAdminService } from '../../services';

const Hospitals = () => {
    const { t } = useTranslation();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    const fetchAdmins = async () => {
        setLoading(true);
        try {
            const res = await superAdminService.getHospitalAdmins();
            setAdmins(res.data || []);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmins();
    }, []);

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({ language: 'en' });
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditing(record);
        form.setFieldsValue({
            name: record.name,
            hospitalName: record.companyName,
            email: record.email,
            address: record.address,
            language: record.language || 'en'
        });
        setModalOpen(true);
    };

    const handleDelete = async (id) => {
        try {
            await superAdminService.deleteHospitalAdmin(id);
            message.success(t('success'));
            fetchAdmins();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (editing) {
                await superAdminService.updateHospitalAdmin(editing._id, values);
            } else {
                await superAdminService.createHospitalAdmin(values);
            }
            message.success(t('success'));
            setModalOpen(false);
            fetchAdmins();
        } catch (error) {
            if (error?.errorFields) return; // validation error
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const columns = [
        { title: t('name'), dataIndex: 'name', key: 'name' },
        { title: t('hospitalName') || 'Hospital', dataIndex: 'companyName', key: 'companyName' },
        { title: t('phoneNumber'), dataIndex: 'phoneNumber', key: 'phoneNumber' },
        { title: t('branches') || 'Branches', dataIndex: 'branchCount', key: 'branchCount', render: (v) => <Tag color="blue">{v || 0}</Tag> },
        { title: t('staff') || 'Staff', dataIndex: 'staffCount', key: 'staffCount', render: (v) => <Tag color="green">{v || 0}</Tag> },
        {
            title: t('status') || 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => isActive ? <Tag color="green">{t('active') || 'Active'}</Tag> : <Tag>{t('inactive') || 'Inactive'}</Tag>
        },
        {
            title: t('actions') || 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    <Popconfirm title={t('confirmDelete') || 'Delete?'} onConfirm={() => handleDelete(record._id)}>
                        <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className="page-container">
            <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
                <Col><h1 style={{ margin: 0 }}><BankOutlined /> {t('hospitals') || 'Hospitals'}</h1></Col>
                <Col>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        {t('createHospitalAdmin') || 'Create Hospital Admin'}
                    </Button>
                </Col>
            </Row>

            <Card>
                <Table
                    columns={columns}
                    dataSource={admins}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 800 }}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            <Modal
                title={editing ? (t('edit') || 'Edit') : (t('createHospitalAdmin') || 'Create Hospital Admin')}
                open={modalOpen}
                onOk={handleSubmit}
                onCancel={() => setModalOpen(false)}
                okText={t('save') || 'Save'}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="hospitalName" label={t('hospitalName') || 'Hospital Name'}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="phoneNumber" label={t('phoneNumber')} rules={[{ required: !editing }]}>
                        <Input disabled={!!editing} />
                    </Form.Item>
                    {!editing && (
                        <Form.Item name="password" label={t('password')} rules={[{ required: true }]}>
                            <Input.Password />
                        </Form.Item>
                    )}
                    <Form.Item name="email" label={t('email') || 'Email'}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="address" label={t('address') || 'Address'}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="language" label={t('language') || 'Language'}>
                        <Select
                            options={[
                                { value: 'en', label: 'English' },
                                { value: 'ar', label: 'العربية' },
                                { value: 'ku', label: 'کوردی' }
                            ]}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Hospitals;
